import * as AccordionPrimitive from '@radix-ui/react-accordion';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceRow } from '@/components/AbsenceRow';
import { UnreadDot } from '@/components/Dashboard/UnreadDot';
import { buildAbsenceDateLabel } from '@/components/Dashboard/absenceDateLabel';
import { ChevronDown } from '@/components/Icons/ChevronDown';
import type { NonAttendance, NonAttendanceGroup } from '@/types/overview';

interface Props {
  group: NonAttendanceGroup;
  value: string;
  onAbsenceClick?: (absence: NonAttendance, date: string) => void;
}

export const AbsenceDateGroup: React.FC<Props> = ({
  group,
  value,
  onAbsenceClick,
}) => {
  const { t } = useTranslation();
  const hasUnread = group.nonAttendances.some(({ isNew }) => isNew);
  const dateLabel = buildAbsenceDateLabel(
    group.date,
    t('home.dashboard.absences.today'),
    t('home.dashboard.absences.yesterday'),
  );

  return (
    <AccordionPrimitive.Item
      value={value}
      className="border-b border-border last:border-b-0"
    >
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger className="group flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-4 text-left outline-none md:px-5 md:py-6">
          <span className="text-subhead-14 flex min-w-0 flex-1 items-center gap-2 text-foreground">
            {hasUnread && <UnreadDot />}
            <span className="truncate">{dateLabel}</span>
          </span>
          <span className="text-body-13 md:text-body-14 flex shrink-0 items-center gap-2 text-muted-foreground">
            {t('home.dashboard.absences.count', {
              count: group.nonAttendances.length,
            })}
            <ChevronDown className="size-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </span>
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
      <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        <ul className="flex flex-col">
          {group.nonAttendances.map((nonAttendance) => (
            <AbsenceRow
              key={nonAttendance.id}
              nonAttendance={nonAttendance}
              onClick={
                onAbsenceClick &&
                (() => onAbsenceClick(nonAttendance, group.date))
              }
            />
          ))}
        </ul>
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  );
};
